import {Hidden, Snackbar} from '@material-ui/core';
import ExportApi from 'api/query/export/export-api';
import {ExportFilters} from 'api/query/export/export-filters';
import Button from 'components/button';
import {ExportOutlinedIcon} from 'components/icons/export-outlined';
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigation} from 'react-navi';
import {toQuerystring} from 'utils/navigation-utils';
import {useAuth0} from '@auth0/auth0-react';
import {ExportButtonConstant} from './export-button-constant';
import {ExportButtonProps} from './export-button-props';
import './export-button.scss';

/**
 * A button to export search results.
 */
export const ExportButton: React.FC<ExportButtonProps> = (props) => {
  const [showToast, setShowToast] = useState(false);
  const { isAuthenticated } = useAuth0();
  const [toastMessage, setToastMessage] = useState<string[]>(ExportButtonConstant.SuccessMessage);
  const [loading, setLoading] = useState(false);
  const navigation = useNavigation();

  const {
    user,
    db,
    scenario,
    filters,
    catalogueId,
    customASBV,
  } = props;

  /**
   * Calls api to export csv to email.
   */
  const exportCsv = useCallback(async () => {
    setLoading(true);
    const payload: ExportFilters = {
      userType: user,
      dbType: db,
      scenario,
      filters,
      catalogueId,
      customASBV,
    };

    try {
      let searchTitle = localStorage.getItem('searchTitle');
      if (!searchTitle) {
        searchTitle = 'Search Results';
      }
      await ExportApi.export(payload, searchTitle);
      setToastMessage(ExportButtonConstant.SuccessMessage);
    } catch {
      setToastMessage(ExportButtonConstant.ErrorMessage);
    } finally {
      setShowToast(true);
      setLoading(false);
    }
  }, [
    catalogueId,
    customASBV,
    db, filters,
    scenario,
    user]);

  /**
   * Checks if user is authenticated if not, redirects to login page, other way calls export of csv.
   */
  const tryToExport = useCallback(async () => {
    const loginUrl = '/account/login';

    if (isAuthenticated) {
      await exportCsv();
    } else {
      navigation.navigate('/account/login');
    }
  }, [exportCsv, navigation, isAuthenticated]);

  /**
   * Exports csv on component mount if url query includes export.
   */
  useEffect(() => {
    const { pathname, query } = navigation.getCurrentValue().url;
    // triggers export if action is included in url
    if (query.export) {
      tryToExport();
      // removes export from address bar to prevent sharing link with export action and updates url
      delete query.export;
      navigation.navigate(`${pathname}${toQuerystring(query)}`, { replace: true }); // big mystery why 'navigate' does not cause reloading
    }
  }, [navigation, tryToExport]);

  return (
    <Hidden smDown>
      <Button
        className="sg-export-button"
        variant="text"
        loading={loading}
        onClick={() => tryToExport()}
      >
        <ExportOutlinedIcon /> Export
      </Button>
      <Snackbar
        open={showToast}
        onClose={() => setShowToast(false)}
        autoHideDuration={null}
      >
        <div className="sg-export-button__snackbar">
          <div>{toastMessage.map((message: string) => <p key={message}>{message}</p>)}</div>
          <Button
            variant="text"
            onClick={() => setShowToast(false)}
          >Dismiss
          </Button>
        </div>
      </Snackbar>
    </Hidden>
  );
};
